import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, IconButton,
  Box, Drawer, List, ListItem, ListItemText,
  Divider, useMediaQuery, Collapse, Paper, Popper,
  Grow, ClickAwayListener, MenuList, MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import logo from '../images/SN (1).png';
import '../App.css';

/* ── Brand tokens ─────────────────────────────── */
const BRAND = '#233098';          // SN brand navy
const BRAND_DARK = '#1a2580';
const TOP_BG = '#233098';         // brand navy top bar
const NAV_BG = '#ffffff';         // clean white navbar
const NAV_BORDER = '#dde4f5';

const softwareLinks = [
  { label: 'Education CRM', path: '/education-crm' },
  { label: 'School Management Software', path: '/school-management' },
  { label: 'Real Estate CRM', path: '/real-estate-crm' },
  { label: 'Real Estate Listing Site', path: '/real-estate-listing' },
  { label: 'Biometric Attendance & Payroll', path: '/biometric-payroll' },
];

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Softwares', path: '#' },
  { name: 'About Us', path: '/abouthome' },
  { name: 'Services', path: '/servicehome' },
  { name: 'Contact Us', path: '/contacthome' },
];

const socialLinks = [
  { icon: <FacebookIcon sx={{ fontSize: 15 }} />, href: '#' },
  { icon: <TwitterIcon sx={{ fontSize: 15 }} />, href: '#' },
  { icon: <LinkedInIcon sx={{ fontSize: 15 }} />, href: 'https://www.linkedin.com/company/103931743/' },
  { icon: <InstagramIcon sx={{ fontSize: 15 }} />, href: '#' },
  { icon: <YouTubeIcon sx={{ fontSize: 15 }} />, href: '#' },
];

/* ══════════════════════════════════════════════════
   HEADER COMPONENT
══════════════════════════════════════════════════ */
const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [softwaresOpen, setSoftwaresOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const anchorRef = useRef(null);
  const closeTimer = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isSoftwareActive = softwareLinks.some(s => isActive(s.path));
  const closeDrawer = () => setDrawerOpen(false);

  /* scroll shadow */
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  /* hover-safe open/close with 150ms buffer */
  const openDrop = () => { clearTimeout(closeTimer.current); setDropOpen(true); };
  const startClose = () => { closeTimer.current = setTimeout(() => setDropOpen(false), 150); };
  const keepOpen = () => clearTimeout(closeTimer.current);
  const closeDrop = () => { clearTimeout(closeTimer.current); setDropOpen(false); };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <AppBar
      position="sticky" elevation={0}
      sx={{
        bgcolor: 'transparent', backgroundImage: 'none',
        zIndex: theme.zIndex.appBar + 10,
        boxShadow: scrolled ? '0 4px 30px rgba(15,23,42,0.12)' : 'none',
        transition: 'box-shadow 0.3s',
      }}
    >

      {/* ╔══════════════════════════════════════════╗
          ║  TOP INFO BAR — deep slate #0f172a        ║
          ╚══════════════════════════════════════════╝ */}
      <Box sx={{
        bgcolor: TOP_BG,
        display: { xs: 'none', md: 'block' },
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { md: 3, lg: 5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 40 }}>

            {/* Contact pills */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              {[
                { icon: <LocationOnIcon sx={{ fontSize: 13 }} />, label: 'Basavanagudi, Karnataka, India' },
                { icon: <PhoneIcon sx={{ fontSize: 13 }} />, label: '(+91) 9740857368', href: 'tel:+919740857368' },
                { icon: <EmailIcon sx={{ fontSize: 13 }} />, label: 'info@sninfosystems.com', href: 'mailto:info@sninfosystems.com' },
              ].map((item, i) => (
                <Box
                  key={i}
                  component={item.href ? 'a' : 'div'}
                  href={item.href}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.8,
                    color: 'rgba(255,255,255,0.65)',
                    textDecoration: 'none', cursor: item.href ? 'pointer' : 'default',
                    '&:hover': item.href ? { color: '#fff' } : {},
                    transition: 'color 0.2s',
                  }}
                >
                  <Box sx={{ color: '#60a5fa', display: 'flex', flexShrink: 0 }}>{item.icon}</Box>
                  <Typography sx={{ fontSize: 12.5, fontFamily: "'Montserrat',sans-serif", fontWeight: 400, whiteSpace: 'nowrap' }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Social icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.35)', fontFamily: "'Montserrat',sans-serif", mr: 1 }}>
                Follow Us:
              </Typography>
              {socialLinks.map((s, i) => (
                <IconButton key={i} size="small"
                  component="a" href={s.href} target="_blank" rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(255,255,255,0.5)', p: 0.6, borderRadius: '7px',
                    '&:hover': { color: '#60a5fa', bgcolor: 'rgba(255,255,255,0.07)' },
                    transition: 'all 0.18s',
                  }}
                >
                  {s.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ╔══════════════════════════════════════════╗
          ║  MAIN NAVBAR — white                     ║
          ╚══════════════════════════════════════════╝ */}
      <Box sx={{
        bgcolor: NAV_BG,
        borderBottom: `1px solid ${NAV_BORDER}`,
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 3, lg: 5 } }}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 68 }, height: { xs: 64, md: 68 }, justifyContent: 'space-between', gap: 2, overflow: 'visible' }}>

            {/* Logo — tall image, navbar stays compact via negative margins */}
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
              <img src={logo} alt="SN Info Systems" style={{ height: 106, width: 'auto', display: 'block', marginTop: '-16px', marginBottom: '-16px' }} />
            </Link>

            {/* ──── Desktop nav ──── */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

                {navLinks.map((item, idx) =>
                  item.name === 'Softwares' ? (

                    /* ─── Softwares Dropdown ─── */
                    <Box key={idx}>
                      <Button
                        ref={anchorRef}
                        onMouseEnter={openDrop}
                        onMouseLeave={startClose}
                        endIcon={
                          <KeyboardArrowDownIcon sx={{
                            fontSize: '17px !important', ml: '-5px',
                            transition: 'transform 0.25s',
                            transform: dropOpen ? 'rotate(180deg)' : 'rotate(0)',
                          }} />
                        }
                        sx={{
                          fontFamily: "'Montserrat',sans-serif",
                          fontWeight: (isSoftwareActive || dropOpen) ? 700 : 600,
                          fontSize: 14.5, textTransform: 'none', letterSpacing: 0,
                          color: (isSoftwareActive || dropOpen) ? BRAND : '#1e293b',
                          bgcolor: (isSoftwareActive || dropOpen) ? `${BRAND}0d` : 'transparent',
                          px: 1.8, py: 0.8, borderRadius: '10px', minHeight: 0,
                          position: 'relative',
                          '&:hover': { bgcolor: `${BRAND}0d`, color: BRAND },
                          transition: 'all 0.18s',
                          /* Active underline */
                          '&::after': isSoftwareActive ? {
                            content: '""', position: 'absolute',
                            bottom: 5, left: '16%', right: '16%',
                            height: 2.5, bgcolor: BRAND, borderRadius: 2,
                          } : {},
                        }}
                      >
                        Softwares
                      </Button>

                      {/* MUI Popper dropdown — hover-safe */}
                      <Popper
                        open={dropOpen}
                        anchorEl={anchorRef.current}
                        placement="bottom-start"
                        transition disablePortal={false}
                        style={{ zIndex: 1500 }}
                      >
                        {({ TransitionProps }) => (
                          <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                            <Paper
                              elevation={0}
                              onMouseEnter={keepOpen}
                              onMouseLeave={startClose}
                              sx={{
                                mt: 0.8, minWidth: 258,
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 20px 60px rgba(15,23,42,0.14)',
                                overflow: 'hidden',
                              }}
                            >
                              {/* Gradient top accent */}
                              <Box sx={{ height: 3.5, background: `linear-gradient(90deg, ${BRAND}, #8b5cf6)` }} />

                              <ClickAwayListener onClickAway={closeDrop}>
                                <MenuList autoFocusItem={false} sx={{ py: 1 }}>
                                  {softwareLinks.map((opt, i) => {
                                    const active = isActive(opt.path);
                                    return (
                                      <MenuItem
                                        key={i}
                                        component={Link} to={opt.path}
                                        onClick={closeDrop}
                                        sx={{
                                          fontFamily: "'Montserrat',sans-serif",
                                          fontSize: 13.5,
                                          fontWeight: active ? 700 : 500,
                                          color: active ? BRAND : '#334155',
                                          px: 2.5, py: 1.1,
                                          gap: 1.2,
                                          borderLeft: active ? `3px solid ${BRAND}` : '3px solid transparent',
                                          bgcolor: active ? `${BRAND}0d` : 'transparent',
                                          '&:hover': {
                                            bgcolor: `${BRAND}0d`,
                                            color: BRAND,
                                            borderLeft: `3px solid ${BRAND}`,
                                          },
                                          transition: 'all 0.15s',
                                          display: 'flex', alignItems: 'center',
                                        }}
                                      >
                                        {active && (
                                          <ArrowRightAltIcon sx={{ fontSize: 16, color: BRAND, flexShrink: 0 }} />
                                        )}
                                        {opt.label}
                                      </MenuItem>
                                    );
                                  })}
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>
                    </Box>

                  ) : (
                    /* Regular nav button */
                    <Button
                      key={idx}
                      component={Link} to={item.path}
                      sx={{
                        fontFamily: "'Montserrat',sans-serif",
                        fontWeight: isActive(item.path) ? 700 : 600,
                        fontSize: 14.5, textTransform: 'none', letterSpacing: 0,
                        color: isActive(item.path) ? BRAND : '#1e293b',
                        bgcolor: isActive(item.path) ? `${BRAND}0d` : 'transparent',
                        px: 1.8, py: 0.8, borderRadius: '10px', minHeight: 0,
                        position: 'relative',
                        '&:hover': { bgcolor: `${BRAND}0d`, color: BRAND },
                        transition: 'all 0.18s',
                        '&::after': isActive(item.path) ? {
                          content: '""', position: 'absolute',
                          bottom: 5, left: '16%', right: '16%',
                          height: 2.5, bgcolor: BRAND, borderRadius: 2,
                        } : {},
                      }}
                    >
                      {item.name}
                    </Button>
                  )
                )}

                {/* ── CTA button ── */}
                <Button
                  variant="contained"
                  component={Link} to="/contacthome"
                  startIcon={<CalendarMonthIcon sx={{ fontSize: '17px !important' }} />}
                  sx={{
                    ml: 2, flexShrink: 0,
                    bgcolor: BRAND,
                    color: '#fff',
                    fontFamily: "'Montserrat',sans-serif",
                    fontWeight: 700, fontSize: 14, textTransform: 'none',
                    px: 3, py: 1.1, borderRadius: '12px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 4px 18px rgba(35,48,152,0.3)',
                    '&:hover': {
                      bgcolor: BRAND_DARK,
                      boxShadow: '0 6px 28px rgba(35,48,152,0.42)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.22s',
                  }}
                >
                  Make Appointment
                </Button>
              </Box>
            )}

            {/* Mobile hamburger */}
            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  color: BRAND, bgcolor: `${BRAND}12`,
                  borderRadius: '10px', p: 0.9,
                  '&:hover': { bgcolor: `${BRAND}20` },
                }}
              >
                <MenuIcon sx={{ fontSize: 23 }} />
              </IconButton>
            )}
          </Toolbar>
        </Box>
      </Box>

      {/* ╔══════════════════════════════════════════╗
          ║  MOBILE DRAWER                           ║
          ╚══════════════════════════════════════════╝ */}
      <Drawer anchor="left" open={drawerOpen} onClose={closeDrawer}
        PaperProps={{ sx: { width: 270, bgcolor: '#fff', display: 'flex', flexDirection: 'column' } }}>

        {/* Drawer header */}
        <Box sx={{
          bgcolor: TOP_BG,
          px: 2.5, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ bgcolor: NAV_BG, borderRadius: '10px', p: 0.5, height: 50, width: 50 }}>
            <Link to="/" onClick={closeDrawer}>
              <img src={logo} alt="SN Info Systems" style={{ height: "100%", width: 'auto', }} />
            </Link>
          </Box>
          <IconButton onClick={closeDrawer} size="small"
            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', borderRadius: '8px', p: 0.7 }}>
            <CloseIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Box>

        {/* Contact strip in drawer */}
        <Box sx={{ bgcolor: '#f8fafc', px: 2.5, py: 1.5, borderBottom: '1px solid #e8ecf0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 13, color: BRAND }} />
            <Typography component="a" href="tel:+919740857368"
              sx={{ fontSize: 13, color: BRAND, fontFamily: "'Montserrat',sans-serif", textDecoration: 'none', fontWeight: 600 }}>
              (+91) 9740857368
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 13, color: '#64748b' }} />
            <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: "'Montserrat',sans-serif" }}>
              info@sninfosystems.com
            </Typography>
          </Box>
        </Box>

        {/* Nav list */}
        <List sx={{ px: 1.5, pt: 1.5, flex: 1 }}>
          {navLinks.map((item, idx) => {
            const active = item.name !== 'Softwares' ? isActive(item.path) : isSoftwareActive;
            return (
              <React.Fragment key={idx}>
                <ListItem
                  button
                  component={item.name === 'Softwares' ? 'div' : Link}
                  to={item.name !== 'Softwares' ? item.path : undefined}
                  onClick={item.name === 'Softwares' ? () => setSoftwaresOpen(p => !p) : closeDrawer}
                  sx={{
                    borderRadius: '10px', mb: 0.3, py: 1,
                    bgcolor: active ? `${BRAND}0e` : 'transparent',
                    borderLeft: active ? `3px solid ${BRAND}` : '3px solid transparent',
                    '&:hover': { bgcolor: `${BRAND}0e` },
                    transition: 'all 0.15s',
                  }}
                >
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontFamily: "'Montserrat',sans-serif",
                      fontSize: 14.5,
                      fontWeight: active ? 700 : 600,
                      color: active ? BRAND : '#1e293b',
                    }}
                  />
                  {item.name === 'Softwares' && (
                    <Box sx={{ color: '#94a3b8' }}>
                      {softwaresOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                  )}
                </ListItem>

                {item.name === 'Softwares' && (
                  <Collapse in={softwaresOpen} timeout="auto" unmountOnExit>
                    <List disablePadding sx={{ pl: 2, mb: 0.5 }}>
                      {softwareLinks.map((sub, i) => {
                        const sa = isActive(sub.path);
                        return (
                          <ListItem button key={i}
                            component={Link} to={sub.path} onClick={closeDrawer}
                            sx={{
                              borderRadius: '8px', py: 0.9,
                              borderLeft: sa ? `3px solid ${BRAND}` : '3px solid transparent',
                              bgcolor: sa ? `${BRAND}0e` : 'transparent',
                              '&:hover': { bgcolor: `${BRAND}0e`, borderLeft: `3px solid ${BRAND}` },
                              transition: 'all 0.15s',
                            }}>
                            <ListItemText primary={sub.label}
                              primaryTypographyProps={{
                                fontFamily: "'Montserrat',sans-serif",
                                fontSize: 13, fontWeight: sa ? 700 : 500,
                                color: sa ? BRAND : '#475569',
                              }} />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>

        <Divider />
        <Box sx={{ px: 2, py: 2 }}>
          <Button variant="contained" fullWidth
            component={Link} to="/contacthome" onClick={closeDrawer}
            startIcon={<CalendarMonthIcon sx={{ fontSize: '17px !important' }} />}
            sx={{
              bgcolor: BRAND,
              color: '#fff', fontFamily: "'Montserrat',sans-serif",
              fontWeight: 700, fontSize: 13.5, textTransform: 'none',
              py: 1.3, borderRadius: '10px',
              boxShadow: '0 4px 16px rgba(35,48,152,0.3)',
              '&:hover': { bgcolor: BRAND_DARK },
            }}>
            Make Appointment
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.8, mt: 2 }}>
            {socialLinks.map((s, i) => (
              <IconButton key={i} size="small"
                component="a" href={s.href} target="_blank"
                sx={{ color: BRAND, bgcolor: `${BRAND}12`, borderRadius: '8px', p: 0.75, '&:hover': { bgcolor: `${BRAND}22` } }}>
                {s.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
